/**
 * Multi-store picker — wraps MultiSelect with a stores-aware data source.
 *
 * Two restrictions are surfaced here:
 *   - `allowedIds` filters the underlying tenant store list to those the
 *     caller may bind to (store_manager: their own; tenant_admin: undefined
 *     for "all").
 *   - the empty state explains why the picker is empty (no stores in tenant
 *     vs. no stores the caller can act on).
 *
 * Mounting a separate component (over inlining MultiSelect in each callsite)
 * keeps the empty-state copy + the filter logic in one place.
 */
import { useMemo, type JSX } from "react";

import { useStoresQuery, type StoreInfo } from "../../lib/api/stores.js";
import { MultiSelect } from "../ui/multi-select.js";
import type { MultiSelectOption } from "../ui/multi-select.helpers.js";

interface MultiStorePickerProps {
  id?: string;
  value: string[];
  onValueChange: (next: string[]) => void;
  /**
   * Restrict picker options to a specific store-public-id set. Useful for
   * the store_manager invite path (their accessibleStores). Pass `undefined`
   * for an unrestricted picker (tenant_admin).
   */
  allowedIds?: ReadonlyArray<string>;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

function toOption(s: StoreInfo): MultiSelectOption {
  return {
    value: s.id,
    label: s.name,
    description: s.code,
    searchText: `${s.name} ${s.code}`,
  };
}

export function MultiStorePicker({
  id,
  value,
  onValueChange,
  allowedIds,
  placeholder = "Select stores",
  disabled,
  className,
}: MultiStorePickerProps): JSX.Element {
  // Page size 200 — the operator console deliberately doesn't paginate the
  // store picker; tenants with more than a couple hundred stores will need a
  // search-as-you-type variant.
  const storesQuery = useStoresQuery({ pageSize: 200 });

  const allowedSet = useMemo(
    () => (allowedIds ? new Set(allowedIds) : null),
    [allowedIds],
  );

  const options: MultiSelectOption[] = useMemo(() => {
    const items = storesQuery.data?.items ?? [];
    const filtered = allowedSet ? items.filter((s) => allowedSet.has(s.id)) : items;
    return filtered.map(toOption);
  }, [storesQuery.data, allowedSet]);

  const emptyMessage =
    storesQuery.isPending
      ? "Loading stores…"
      : (allowedSet?.size ?? 0) === 0 && allowedIds
        ? "You have no stores assigned."
        : "No matching stores.";

  return (
    <MultiSelect
      id={id}
      options={options}
      value={value}
      onValueChange={onValueChange}
      placeholder={placeholder}
      searchPlaceholder="Search stores"
      emptyMessage={emptyMessage}
      disabled={disabled || storesQuery.isPending}
      className={className}
    />
  );
}
